import React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { DataCollectionAiAgentOperationType, SurveyPageType } from "../../store/Models/Surveys/SurveyDataModel";
import * as SurveyResponseController from "../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { SurveyResponseState } from "../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import { AISurveyAgentState, getActiveAIOperationFromState } from "../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController";
import { isSurveyReadonly } from "../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels";
type PageContext = "instructions" | "questionnaire" | "supplier_questionnaire" | "material" | "ingredient-disclosure";

interface OwnProps {
  pageContext: PageContext;
  className?: string;
}

interface StateProps {
  appServices: ApplicationState["appServices"];
  coreData: SurveyResponseState["coreData"];
  aiSurveyAgentState?: AISurveyAgentState;
}

interface DispatchProps {
  setMainNavPage: (
    pageType: SurveyPageType,
    questionnaireIndex?: number,
    questionnairePageIndex?: number,
    previousPageStack?: number[]
  ) => any;
}

type Props = OwnProps & StateProps & DispatchProps;

const AIButton: React.FC<Props> = ({ appServices, pageContext, className, setMainNavPage, coreData, aiSurveyAgentState }) => {
  const activeAiOperation = getActiveAIOperationFromState(aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey);
  const isInstructionsCta = pageContext === "instructions";
  const buttonLabelKey = "Auto-answer with AI";
  const aiDocProcessorLabel = appServices?.localizeString(buttonLabelKey) ?? buttonLabelKey;
  const hasResponseBeenSubmittedOrIsBeingSubmitted = (): boolean => (
    isSurveyReadonly(coreData, activeAiOperation) || (coreData?.response.responseDataJson && JSON.parse(coreData.response.responseDataJson).IsOfflineProcessStarted)
  );

  const handleClick = () => {
    if (hasResponseBeenSubmittedOrIsBeingSubmitted()) {
      return;
    }
    // Emit analytics
    appServices?.trackEvent("survey.ai_button_clicked", { page_context: pageContext });
    // Navigate to Bulk Inputs (Batch)
    setMainNavPage(SurveyPageType.Batch);
  };

  const buttonStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "fit-content"
  };

  const iconStyle: React.CSSProperties = {
    marginRight: 8,
    fill: "none",
    display: "inline-block",
    transform: "translateY(2px)"
  };
  
  const isDisabled = hasResponseBeenSubmittedOrIsBeingSubmitted();
  const buttonClass = `btn btn-toxnot-primary ${className ?? ""}`;

  return (
    <button
      type="button"
      aria-label={aiDocProcessorLabel}
      className={buttonClass}
      disabled={isDisabled}
      style={isInstructionsCta
        ? { ...buttonStyle, backgroundColor: "#e16838", borderColor: "#e16838", color: "#ffffff" }
        : buttonStyle}
      onClick={handleClick}
    >
      <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="18" height="18" viewBox="0 0 30 30" style={iconStyle} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-lov-id="src/pages/Instructions.tsx:180:18" data-lov-name="Sparkles" data-component-path="src/pages/Instructions.tsx" data-component-line="180" data-component-file="Instructions.tsx" data-component-name="Sparkles" data-component-content="%7B%22className%22%3A%22w-4%20h-4%20ml-2%22%7D"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></svg>
      {aiDocProcessorLabel}
    </button>
  );
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
  appServices: state.appServices,
  coreData: state.surveyResponseState ? (state.surveyResponseState.coreData ?? null) : null,
  aiSurveyAgentState: state.aiSurveyAgentState
});

const mapDispatchToProps: DispatchProps = {
  setMainNavPage: SurveyResponseController.actionCreators.setMainNavPage
};

export default connect(mapStateToProps, mapDispatchToProps)(AIButton);
